import { useEffect, useState } from 'react'

import { useDispatch } from 'react-redux'

import { OffPlatformSubmissionMetricsUnavailableNotice } from '@components/Submission/Influencer/Profile/OffPlatformMetricUnavailableNotice'
import { SubmissionChannels } from '@enums'
import { applyAudienceInsightFilters } from '@store/audienceInsights'
import { fetchPrePurchaseAnalytics } from '@store/prePurchaseAnalytics'
import { IOption } from '@store/rootResource/types'
import { AnalyticsBrandConnectPrePurchaseBreakdown } from './BrandConnectPrePurchaseBreakdown'
import { AnalyticsBreakdown } from './Breakdown'
import {
  CONTENT_TYPE_OPTIONS,
  defaultContentTypeOption,
  defaultDurationOption,
  DURATION_OPTIONS,
} from './Filter'
import { usePrePurchaseBreakdown } from './usePrePurchaseBreakdown'

type Props = {
  submissionId: number
}

export const AnalyticsPrePurchaseBreakdown: React.FC<Props> = ({ submissionId }) => {
  const dispatch = useDispatch()
  const {
    identityId,
    analyticsType,
    isFetching,
    analytics,
    currency,
    socialSubmissionType,
    channel,
    mediaType,
    isProxy,
    isAnalyticsEmpty,
    isYouTubeBrandConnect,
    brandConnectMetrics,
  } = usePrePurchaseBreakdown(submissionId)

  const [duration, setDuration] = useState<IOption>(defaultDurationOption)
  const [contentType, setContentType] = useState<IOption>(defaultContentTypeOption)

  useEffect(() => {
    dispatch(fetchPrePurchaseAnalytics(identityId, submissionId, analyticsType))
  }, [identityId, submissionId, analyticsType])

  // Apply filters to audience insights when duration or contentType changes
  useEffect(() => {
    if (isYouTubeBrandConnect) {
      dispatch(
        applyAudienceInsightFilters({
          period: duration.value as string,
          content_type: contentType.value as string,
        }),
      )
    }
  }, [duration, contentType, isYouTubeBrandConnect, dispatch])

  if (!isFetching && isProxy && isAnalyticsEmpty) {
    return <OffPlatformSubmissionMetricsUnavailableNotice type="prePurchase" />
  }

  const isBrandConnectV2Metrics =
    channel === SubmissionChannels.YouTube &&
    isYouTubeBrandConnect

  if (isBrandConnectV2Metrics) {
    return (
      <AnalyticsBrandConnectPrePurchaseBreakdown
        analytics={brandConnectMetrics}
        isLoaded={!isFetching}
        currency={currency}
        socialSubmissionType={socialSubmissionType}
        channel={channel as SubmissionChannels}
        mediaType={mediaType}
        isProxy={isProxy}
        filters={{
          period: {
            filterLabel: 'Duration',
            onChange: setDuration,
            options: DURATION_OPTIONS,
            selected: duration,
          },
          content_type: {
            filterLabel: 'Content Type',
            onChange: setContentType,
            options: CONTENT_TYPE_OPTIONS,
            selected: contentType,
          },
        }}
      />
    )
  }

  return (
    <AnalyticsBreakdown
      analytics={analytics}
      perspective="pre-purchase"
      isLoaded={!isFetching}
      currency={currency}
      socialSubmissionType={socialSubmissionType}
      channel={channel as SubmissionChannels}
      mediaType={mediaType}
      isProxy={isProxy}
    />
  )
}

export default AnalyticsPrePurchaseBreakdown
